import React, { createContext, useContext, useState, ReactNode } from 'react';

type ModalType = 'alert' | 'confirm';

type ModalRequest = {
  id: string;
  type: ModalType;
  title?: string;
  message: string;
  resolve: (value?: any) => void;
};

const ModalCtx = createContext<{
  showAlert: (message: string, title?: string) => Promise<void>;
  showConfirm: (message: string, title?: string) => Promise<boolean>;
} | null>(null);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<ModalRequest[]>([]);

  const processNext = () => setQueue(q => q.slice(1));

  const showAlert = (message: string, title?: string) => {
    return new Promise<void>((resolve) => {
      const req: ModalRequest = { id: String(Date.now()) + Math.random(), type: 'alert', title, message, resolve };
      setQueue(q => [...q, req]);
    });
  };

  const showConfirm = (message: string, title?: string) => {
    return new Promise<boolean>((resolve) => {
      const req: ModalRequest = { id: String(Date.now()) + Math.random(), type: 'confirm', title, message, resolve };
      setQueue(q => [...q, req]);
    });
  };

  const current = queue[0] || null;

  return (
    <ModalCtx.Provider value={{ showAlert, showConfirm }}>
      {children}

      {/* Modal UI (simple, centrally styled) */}
      {current && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-2xl w-full max-w-lg p-6">
            {current.title && <h3 className="text-lg font-semibold mb-2">{current.title}</h3>}
            <div className="text-sm text-ink-text dark:text-ink-text-dark mb-4 whitespace-pre-wrap">{current.message}</div>
            <div className="flex justify-end gap-3">
              {current.type === 'confirm' && (
                <button
                  onClick={() => { current.resolve(false); processNext(); }}
                  className="px-4 py-2 rounded-md bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:bg-surface-dark-alt"
                >
                  Cancelar
                </button>
              )}
              <button
                onClick={() => { current.resolve(current.type === 'confirm' ? true : undefined); processNext(); }}
                className="px-4 py-2 rounded-md bg-brand-500 text-white hover:bg-brand-600"
              >
                {current.type === 'confirm' ? 'Confirmar' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalCtx.Provider>
  );
};

export const useModal = () => {
  const ctx = useContext(ModalCtx);
  if (!ctx) throw new Error('useModal must be used within ModalProvider');
  return ctx;
};

export default ModalProvider;
